import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Typography } from 'antd';
import { User, Group, UserMultiple, Building } from '@carbon/icons-react';
import PageContainer from 'components/PageContainer/PageContainer';
import PageTitleBar from 'components/PageTitleBar/PageTitleBar';
import Card from 'components/Card/Card';
import { RevealingLink } from 'components/ui/RevealingLink/RevealingLink';
import PageMeta from 'components/PageMeta/PageMeta';

export default function NewApplication() {
  const history = useHistory();

  return (
    <div className="h-full flex flex-col">
      <PageMeta title="MCCYN Fee Assistance" />

      <PageContainer>
        <Card
          noBodyPadding
          bordered={false}
          className="md:border md:border-solid md:border-[#e7e7e7] w-screen md:w-full"
        >
          <PageTitleBar title="MCCYN Fee Assistance" className="rounded-none md:rounded-t-2xl" />

          <div className="p-7">
            <Typography.Title level={3} className="!font-bold !mb-2">
              What you'll need
            </Typography.Title>

            <Typography.Text className="block text-gray-600 mb-8">
              We'll guide you through what applies to your family. Here's what to expect.
            </Typography.Text>

            <div className="space-y-4 mb-8">
              <div className="bg-gray-50 p-6 rounded-xl flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-blue-700" />
                </div>
                <div>
                  <Typography.Title level={5} className="!mb-1 !font-semibold">
                    Sponsor verification
                  </Typography.Title>
                  <Typography.Text className="text-gray-600">
                    Pay documentation and orders, if applicable
                  </Typography.Text>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Group size={24} className="text-blue-700" />
                </div>
                <div>
                  <Typography.Title level={5} className="!mb-1 !font-semibold">
                    Spouse or domestic partner
                  </Typography.Title>
                  <Typography.Text className="text-gray-600">Income or school enrollment</Typography.Text>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <UserMultiple size={24} className="text-blue-700" />
                </div>
                <div>
                  <Typography.Title level={5} className="!mb-1 !font-semibold">
                    Child verification
                  </Typography.Title>
                  <Typography.Text className="text-gray-600">
                    Verification for each child you are applying for
                  </Typography.Text>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl flex gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Building size={24} className="text-blue-700" />
                </div>
                <div>
                  <Typography.Title level={5} className="!mb-1 !font-semibold">
                    Provider information
                  </Typography.Title>
                  <Typography.Text className="block text-gray-600 mb-1">
                    Cost verification from your provider
                  </Typography.Text>
                  <Typography.Text className="text-gray-500 text-sm">
                    Don't have a provider yet? Call us at 1-800-424-2246
                  </Typography.Text>
                </div>
              </div>
            </div>

            <RevealingLink
              className="mb-8"
              text="What documents might I need?"
              expandedText="Hide"
              reveals={
                <div className="bg-white border border-solid border-gray-200 rounded-lg">
                  <div className="p-6">
                    <Typography.Title level={5} className="!font-semibold !mb-3 !text-base">
                      Sponsor
                    </Typography.Title>

                    <div className="text-gray-500 text-sm leading-relaxed">
                      <p className="mb-2">Earnings statement (such as LES)</p>
                      <p className="mb-2">Orders, if Guard/Reserve or deployed</p>
                      <p>Notification of Personnel Action, if applicable</p>
                    </div>
                  </div>

                  <div className="p-6 border-t border-solid border-gray-100">
                    <Typography.Title level={5} className="!font-semibold !mb-3 !text-base">
                      Spouse or Domestic Partner
                    </Typography.Title>

                    <div className="text-gray-500 text-sm leading-relaxed">
                      <p className="mb-2">If working: recent pay stubs</p>
                      <p className="mb-2">If student: current school schedule</p>
                      <p>If self-employed or job seeking: documentation may vary</p>
                    </div>
                  </div>

                  <div className="p-6 border-t border-solid border-gray-100">
                    <Typography.Title level={5} className="!font-semibold !mb-3 !text-base">
                      Child
                    </Typography.Title>

                    <div className="text-gray-500 text-sm leading-relaxed">
                      <p>Verification for each eligible dependent you are applying for</p>
                    </div>
                  </div>

                  <div className="p-6 border-t border-solid border-gray-100">
                    <Typography.Title level={5} className="!font-semibold !mb-3 !text-base">
                      Provider
                    </Typography.Title>

                    <div className="text-gray-500 text-sm leading-relaxed">
                      <p className="mb-2">Cost verification form signed by you and your provider</p>
                      <p>Includes rates, schedule, and any discounts</p>
                    </div>
                  </div>

                  <div className="py-3 px-6 border-t border-solid border-gray-100 bg-[rgb(249,250,251)] text-gray-400 text-xs leading-relaxed rounded-b-lg">
                    This is a general guide—the application will walk you through exactly what applies to your
                    situation, based on your responses.
                  </div>
                </div>
              }
            />

            <Button
              type="primary"
              size="large"
              className="w-full h-[56px] rounded-lg font-semibold text-lg"
              onClick={() => {
                history.push('/family/applications/new');
              }}
            >
              Get Started
            </Button>

            <Typography.Text className="block text-center mt-4 text-sm max-w-[600px] mx-auto text-gray-500">
              Don't have everything yet? You can save and come back anytime, and your checklist will show what you still
              need. Average time to complete is about 15 minutes
            </Typography.Text>
          </div>
        </Card>
      </PageContainer>
    </div>
  );
}
